import type { HTMLAttributes, MouseEventHandler, Ref } from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '#core/utils/cn.ts'
import UserFilled from '#components/primitives/icons/user-filled.tsx'
import { Tooltip } from '#ynt-design-system/components/tooltip/index.ts'
import { LoadingSpinner } from '#ynt-design-system/components/loading-spinner/index.ts'
import { avatarColor, avatarInitial } from '#ynt-design-system/components/avatar/avatar-identity.ts'
import type { AvatarColor } from '#ynt-design-system/components/avatar/avatar-identity.ts'

export type AvatarSize = 'extra-small' | 'small' | 'medium' | 'large'
const COLOR_CLASS: Record<AvatarColor, string> = {
  informative: 'ynt-avatar-informative',
  warning: 'ynt-avatar-warning',
  brand: 'ynt-avatar-brand',
  teal: 'ynt-avatar-teal',
  pink: 'ynt-avatar-pink',
  success: 'ynt-avatar-success',
}

const VARIANT_CLASS = {
  loading: 'ynt-avatar-loading',
  image: 'ynt-avatar-image',
  initials: 'ynt-avatar-monogram',
  empty: 'ynt-avatar-empty',
}

export const AVATAR_SIZE_CLASS: Record<AvatarSize, string> = {
  'extra-small': 'ynt-avatar-extra-small',
  small: 'ynt-avatar-small',
  medium: 'ynt-avatar-medium',
  large: 'ynt-avatar-large',
}

export interface AvatarProps extends Omit<HTMLAttributes<HTMLElement>, 'color' | 'onClick'> {
  /** The person's display name: the accessible name, the initial and the colour key. Omit for the empty avatar. */
  name?: string
  /** Photo URL. Falls back to the initial when it fails to load. */
  src?: string
  size?: AvatarSize
  /** Overrides the colour derived from `name`. */
  color?: AvatarColor
  loading?: boolean
  /** Rings the avatar to mark the person active. What active means is the caller's: a running timer, being online, … */
  active?: boolean
  /** Set `false` beside the person's visible name, where the hover label would only repeat it. */
  tooltip?: boolean
  /** Renders the avatar as a button, which is what turns on the hover state. */
  onClick?: MouseEventHandler<HTMLElement>
  ref?: Ref<HTMLElement>
}

/**
 * A person's photo, their initial, an empty slot for "no one yet", or a loading placeholder,
 * picked from what is given: `loading`, then a `src` that loads, then `name`, then nothing.
 * Hovering names the person, or offers to assign one on the empty avatar.
 */
export function Avatar({
  name,
  src,
  size = 'small',
  color,
  loading = false,
  active = false,
  tooltip: showTooltip = true,
  onClick,
  className,
  ref,
  'aria-label': ariaLabel,
  ...rest
}: AvatarProps) {
  const { t } = useTranslation('general')
  const [failedSrc, setFailedSrc] = useState<string>()
  const trimmedName = name?.trim()

  const variant = loading
    ? 'loading'
    : src && src !== failedSrc
      ? 'image'
      : trimmedName
        ? 'initials'
        : 'empty'

  const label =
    ariaLabel ?? (variant === 'loading' ? t('avatar.loading') : (trimmedName ?? t('avatar.empty')))

  const tooltip =
    !showTooltip || variant === 'loading' ? undefined : variant === 'empty' ? t('avatar.assign') : trimmedName

  const Root = onClick ? 'button' : 'span'

  const avatar = (
    <Root
      ref={ref as Ref<HTMLButtonElement & HTMLSpanElement>}
      type={onClick ? 'button' : undefined}
      role={onClick ? undefined : 'img'}
      aria-label={label}
      aria-busy={loading || undefined}
      onClick={onClick}
      className={cn(
        'ynt-avatar',
        AVATAR_SIZE_CLASS[size],
        VARIANT_CLASS[variant],
        variant === 'initials' && trimmedName && COLOR_CLASS[color ?? avatarColor(trimmedName)],
        onClick && 'ynt-avatar-interactive',
        active && 'ynt-avatar-active',
        className,
      )}
      {...rest}
    >
      {variant === 'image' && (
        <img src={src} alt="" onError={() => setFailedSrc(src)} />
      )}
      {variant === 'initials' && trimmedName && (
        <span className="ynt-avatar-label" aria-hidden="true">
          {avatarInitial(trimmedName)}
        </span>
      )}
      {variant === 'empty' && <UserFilled className="ynt-avatar-icon" aria-hidden="true" />}
      {variant === 'loading' && <LoadingSpinner className="ynt-avatar-icon" />}
    </Root>
  )

  return tooltip ? <Tooltip label={tooltip}>{avatar}</Tooltip> : avatar
}

export default Avatar
